import { z } from 'zod';
import { APPLICATION_FORMS, BANDO_ACTIVITIES, BANDO_APPLICANTS, BANDO_KINDS, BANDO_SCOPES, BANDO_STATUSES, } from './bando.js';
import { LocalDate, Timestamp } from './common.js';
export const BandoApplicantEnum = z.enum(BANDO_APPLICANTS).meta({ id: 'BandoApplicant' });
export const BandoActivityEnum = z.enum(BANDO_ACTIVITIES).meta({ id: 'BandoActivity' });
export const BandoKindEnum = z.enum(BANDO_KINDS).meta({ id: 'BandoKind' });
export const BandoScopeEnum = z.enum(BANDO_SCOPES).meta({ id: 'BandoScope' });
export const BandoStatusEnum = z.enum(BANDO_STATUSES).meta({ id: 'BandoStatus' });
export const ApplicationFormEnum = z.enum(APPLICATION_FORMS).meta({ id: 'ApplicationForm' });
/** Territorio del bando: province con la sigla (LE, BR, TA…), comuni con il codice ISTAT. */
export const BandoArea = z
    .object({
    scope: BandoScopeEnum,
    provinces: z.array(z.string().length(2)).optional(),
    comuneIds: z.array(z.string().regex(/^\d{6}$/)).optional(),
})
    .meta({ id: 'BandoArea' });
/**
 * Linea ricorrente (es. "Contributi alle Pro Loco" della Regione): esce più o meno ogni anno
 * negli stessi mesi; tra un'edizione e l'altra il sito la mostra "in arrivo".
 */
export const BandoRecurring = z
    .object({
    /** mesi tipici di uscita (1-12) */
    months: z.array(z.int().min(1).max(12)).min(1),
    /** cosa preparare per tempo */
    prepare: z.array(z.string()).optional(),
})
    .meta({ id: 'BandoRecurring' });
/**
 * Requisito da verificare prima di preparare la domanda ("Posso partecipare?"): domanda sì/no;
 * con "no" il sito mostra `fix` (cosa fare o perché non si può).
 */
export const BandoCheck = z
    .object({
    id: z.string().regex(/^[a-z0-9-]{1,40}$/),
    question: z.string().max(300),
    fix: z.string().max(400),
})
    .meta({ id: 'BandoCheck' });
/** Un bando o avviso (`BANDO#<id>/META`), con i dati letti dall'AI e normalizzati. */
export const Bando = z
    .object({
    id: z.string().min(3).max(80),
    sourceId: z.string(),
    /** pagina da cui viene */
    url: z.url(),
    /** pagina ufficiale per fare domanda o leggere l'avviso, se diversa */
    officialUrl: z.url().optional(),
    title: z.string().min(3).max(300),
    /** ente che lo emette (Regione Puglia, Ministero della Cultura…) */
    issuer: z.string().max(200),
    /** riassunto nostro, 2-3 frasi */
    summary: z.string().max(1200),
    /** cosa offre: fondi (senza `kind`) o volontari (Servizio Civile) */
    kind: BandoKindEnum.optional(),
    applicants: z.array(BandoApplicantEnum),
    /** vuoto: vale per tutte le attività */
    activities: z.array(BandoActivityEnum),
    /**
     * che domanda chiede: per un evento, per un'impresa o per un progetto (`applicationForm`);
     * senza, si deduce da chi e cosa
     */
    form: ApplicationFormEnum.optional(),
    area: BandoArea,
    opensAt: LocalDate.optional(),
    deadline: LocalDate.optional(),
    /** importo massimo per progetto, in euro */
    maxAmount: z.number().nonnegative().optional(),
    /** dotazione complessiva, in euro */
    totalBudget: z.number().nonnegative().optional(),
    /** quota coperta dal contributo (es. 80) */
    coveragePercent: z.number().min(0).max(100).optional(),
    howToApply: z.string().max(600).optional(),
    documents: z.array(z.string().max(200)).optional(),
    /** requisiti da verificare (scritti a mano per le linee ricorrenti) */
    checks: z.array(BandoCheck).optional(),
    recurring: BandoRecurring.optional(),
    status: BandoStatusEnum,
    rawS3Key: z.string().optional(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
    /** prima pubblicazione sul sito: da qui partono gli avvisi dei bandi nuovi */
    publishedAt: Timestamp.optional(),
})
    .meta({ id: 'Bando' });
//# sourceMappingURL=bando-model.js.map